import React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { legalEntityClasses } from '../config/calendarTheme';

/**
 * Matter reference node data structure
 */
export interface MatterReferenceData {
  matterId: string;
  displayName: string;
  matterType: 'litigation' | 'transactional' | 'family' | 'criminal' | 'other';
  status: 'active' | 'inactive' | 'closed';
}

/**
 * Serialized matter reference node for JSON storage
 */
export interface SerializedMatterReferenceNode extends Spread<
  {
    matterData: MatterReferenceData;
    type: 'matter-reference';
    version: 1;
  },
  SerializedLexicalNode
> {}

/**
 * Custom Lexical node for #matter references
 * Creates immutable "pill" elements that link to matter records
 */
export class MatterReferenceNode extends DecoratorNode<React.JSX.Element> {
  __matterData: MatterReferenceData;

  static getType(): string {
    return 'matter-reference';
  }

  static clone(node: MatterReferenceNode): MatterReferenceNode {
    return new MatterReferenceNode(node.__matterData, node.__key);
  }

  constructor(matterData: MatterReferenceData, key?: NodeKey) {
    super(key);
    this.__matterData = matterData;
  }

  exportJSON(): SerializedMatterReferenceNode {
    return {
      matterData: this.__matterData,
      type: 'matter-reference',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedMatterReferenceNode): MatterReferenceNode {
    const { matterData } = serializedNode;
    return $createMatterReferenceNode(matterData);
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = legalEntityClasses.matter;
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.JSX.Element {
    return (
      <span
        className={legalEntityClasses.matter}
        title={`Matter: ${this.__matterData.displayName}`}
      >
        📁 #{this.__matterData.displayName}
      </span>
    );
  }

  getTextContent(): string {
    return `#${this.__matterData.displayName}`;
  }

  isImmutable(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }
}

export function $createMatterReferenceNode(matterData: MatterReferenceData): MatterReferenceNode {
  return new MatterReferenceNode(matterData);
}

export function $isMatterReferenceNode(node: LexicalNode | null | undefined): node is MatterReferenceNode {
  return node instanceof MatterReferenceNode;
} 